import React, { useState } from 'react';
import { Bell, MousePointerClick, Timer, Percent, BellOff } from 'lucide-react';
import { usePanelReminders, usePanelTimeseries } from '../../lib/api';
import { DateRange } from '../../lib/panelTypes';
import StatCard from './StatCard';
import TimeSeriesChart from './TimeSeriesChart';
import SeriesModeToggle from './SeriesModeToggle';

const num = (n: number) => (n || 0).toLocaleString();

const NotificationsTab: React.FC<{ range: DateRange }> = ({ range }) => {
  const [cumulative, setCumulative] = useState(false);
  const { data, isLoading } = usePanelReminders(range);
  const { data: ts } = usePanelTimeseries('reminders_sent', range, cumulative);
  const sent = data?.sent ?? 0;
  const tapped = data?.tapped ?? 0;
  const extended = data?.extendedAfterReminder ?? 0;
  const skipped = data?.skipped ?? [];
  const hasData = sent > 0;

  const funnelRows = [
    { label: 'Sent', value: sent },
    { label: 'Tapped', value: tapped },
    { label: 'Extended (signed-in only)', value: extended },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Sent" value={num(sent)} icon={Bell} hint={`${num(data?.failed ?? 0)} failed`} />
        <StatCard label="Tap rate" value={`${(data?.tapRate ?? 0).toFixed(1)}%`} icon={MousePointerClick} hint="tapped / sent" />
        <StatCard label="Extended after reminder" value={num(extended)} icon={Timer} hint={`${(data?.extendRate ?? 0).toFixed(1)}% of signed-in sends`} />
        <StatCard label="Opt-in rate" value={`${(data?.permission.optInRate ?? 0).toFixed(1)}%`} icon={Percent} hint={`${num(data?.permission.granted ?? 0)} granted · ${num(data?.permission.denied ?? 0)} denied`} />
      </div>

      {!hasData ? (
        <div className="bg-white border rounded-lg p-8 text-center">
          <BellOff className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-600 font-medium">No reminders sent in this range yet</p>
          <p className="text-sm text-gray-400 mt-1">
            Expiry reminders fill in once the push-notification feature is live in the stores.
          </p>
        </div>
      ) : (
        <div className="bg-white border rounded-lg p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Sent → tapped → extended</h3>
          <div className="space-y-3">
            {funnelRows.map((row) => (
              <div key={row.label}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700">{row.label}</span>
                  <span className="text-gray-500">
                    {num(row.value)} · {sent > 0 ? ((row.value / sent) * 100).toFixed(0) : 0}%
                  </span>
                </div>
                <div className="h-6 bg-gray-100 rounded overflow-hidden">
                  <div
                    className="h-full bg-amber-500 rounded"
                    style={{ width: `${sent > 0 ? Math.max(2, (row.value / sent) * 100) : 0}%` }}
                  />
                </div>
                {row.label === 'Sent' && (
                  <p className="text-xs text-gray-400 mt-1">
                    {num(data?.sentUser ?? 0)} signed-in · {num(data?.sentGuest ?? 0)} guest — guests cannot extend
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {skipped.length > 0 && (
        <div className="bg-white border rounded-lg p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Skipped reasons</h3>
          <div className="space-y-2">
            {skipped.map((s) => (
              <div key={s.reason} className="flex justify-between text-sm">
                <span className="text-gray-700 capitalize">{s.reason.replace(/_/g, ' ')}</span>
                <span className="text-gray-500">{num(s.count)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border rounded-lg p-5">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h3 className="font-semibold text-gray-900">
            {cumulative ? `Reminders sent since ${range.startDate}` : 'Reminders sent per day'}
          </h3>
          <SeriesModeToggle cumulative={cumulative} onChange={setCumulative} />
        </div>
        <TimeSeriesChart data={ts?.series ?? []} color="#f59e0b" cumulative={cumulative} />
      </div>
      {isLoading && <p className="text-xs text-gray-400">Loading…</p>}
    </div>
  );
};

export default NotificationsTab;
